<script setup lang="ts">
/**
 * WorkspaceRailDot — 진행 레일 축 1칸의 상태 점.
 *
 * 🔴 `empty`(○) 는 **잠금이 아니다** — 043 SC-007("해금·완주 조건을 암시하는 카피·시각 요소 0건,
 *    전 화면")·045 FR-020(병렬 재료)이 순차 잠금 시각을 금지한다. 실제로도 잠긴 축은 0개다
 *    (`canGenerate` 는 성적·진단·검사 중 1종이면 true, `/tests` 는 선행 조건 없음).
 *
 *    그래서 다음을 **하지 않는다**:
 *      - `opacity` 감쇠 — 흐림은 disabled 의 시각 신호다.
 *      - `disabled`·`pointer-events-none`·`cursor-not-allowed` — `○` 축도 즉시 들어갈 수 있다.
 *      - 자물쇠·체인 류 아이콘.
 *    `○` 는 `text-muted` + 점선 테두리로 "아직 비었다"만 말한다(MaterialChecklist 의 EMPTY 어휘와 동일).
 *
 * ## 순서는 점이 아니라 순번·커넥터가 전달한다
 *
 * 점은 **채움 여부만** 말한다. 여정 순서(입력→분석→종합)는 레일의 순번(①②③④)과 축 사이 커넥터
 * 라인이 전달한다 — 점에 순서 의미까지 실으면 "○ 는 아직 못 하는 것"으로 읽혀 잠금 해석이 되살아난다.
 *
 * ## 접근성
 *
 * 점 자체는 `aria-hidden` 이고 의미는 `sr-only` 텍스트로 낸다 — 색·모양만으로 상태를 전달하지 않는다.
 */
import { computed } from 'vue';

import type { RailAxisState } from '#/data/analysis/workspaceRail';

interface Props {
  state: RailAxisState;
}

const props = defineProps<Props>();

/** 상태별 시각 — `next` 만 채운 원(현재 재개 지점), `empty` 는 점선 빈 원. */
const DOT_CLASS: Record<Exclude<RailAxisState, 'unknown'>, string> = {
  filled: 'border-ok bg-ok text-white',
  next: 'border-accent bg-accent text-white',
  empty: 'border-dashed border-line-2 bg-transparent text-muted',
};

/** 스크린리더용 상태명 — 잠금 어휘를 쓰지 않는다. */
const DOT_LABEL: Record<Exclude<RailAxisState, 'unknown'>, string> = {
  filled: '채워짐',
  next: '다음 추천',
  empty: '아직 비어 있음',
};

const visible = computed(() => props.state !== 'unknown');
const dotClass = computed(() => (props.state === 'unknown' ? '' : DOT_CLASS[props.state]));
const dotLabel = computed(() => (props.state === 'unknown' ? '' : DOT_LABEL[props.state]));
</script>

<template>
  <span v-if="visible" class="inline-flex flex-none items-center">
    <span
      class="flex h-[15px] w-[15px] items-center justify-center rounded-full border-[1.5px] transition-colors"
      :class="dotClass"
      aria-hidden="true"
    >
      <!-- filled 만 체크 글리프 — next·empty 는 원의 채움/점선으로 구분된다. -->
      <svg
        v-if="state === 'filled'"
        viewBox="0 0 12 12"
        class="h-[9px] w-[9px]"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M2.5 6.2 4.9 8.6 9.5 3.6" />
      </svg>
    </span>
    <span class="sr-only">{{ dotLabel }}</span>
  </span>
</template>
